// Single access point for platform (web vs. native shell) differences.
// Never read `window.Capacitor` or import a @capacitor/* plugin outside this
// module — mirrors the rule that only `config/env.js` reads import.meta.env.
//
// Native plugins are loaded via dynamic import so the web bundle never pulls
// them in. Every function here is safe to call on web.

import { Capacitor } from '@capacitor/core';
import { useMerchantStore } from '@core/store/merchantStore';
import { env } from '@config/env';

/** True when running inside the Capacitor shell (iOS or Android). */
export function isNative() {
  try {
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
}

/** 'ios' | 'android' | 'web' */
export function platform() {
  try {
    return Capacitor.getPlatform();
  } catch {
    return 'web';
  }
}

export const isIOS = () => platform() === 'ios';
export const isAndroid = () => platform() === 'android';

// Per-merchant native config, generated next to the bundle at build time and
// fetched once at boot. Kept OUT of the JS bundle deliberately: one identical
// bundle has to serve every merchant, or over-the-air updates would need a
// distinct artifact per merchant and lose their point. Capacitor 8 exposes no
// runtime config-read API, hence a plain JSON file. See ADR 0005.
let merchantConfig = null;
let merchantConfigPromise = null;

/**
 * Load `merchant.json` from the native bundle. Idempotent, cached, and safe to
 * call on web (resolves null). Must be awaited before `storefrontHost()` on
 * native — `StoreContextBootstrap` does this before resolving the tenant.
 */
export async function loadMerchantConfig() {
  if (!isNative()) return null;
  if (merchantConfig) return merchantConfig;

  if (!merchantConfigPromise) {
    merchantConfigPromise = fetch('/merchant.json')
      .then((response) => (response.ok ? response.json() : null))
      .catch(() => null)
      .then((config) => {
        merchantConfig = config;
        return config;
      });
  }
  return merchantConfigPromise;
}

/** Test seam. Clears the cached merchant config. */
export function resetMerchantConfig() {
  merchantConfig = null;
  merchantConfigPromise = null;
}

/**
 * Host that identifies this storefront to the backend.
 *
 * The tenant is resolved from the domain the storefront is served on. That
 * works on web, but the native shell runs on capacitor://localhost (iOS) /
 * https://localhost (Android), and `localhost` is not a registered store — the
 * resolve call 404s and the app renders "Account Not Found". Native builds
 * therefore report their merchant's deployed web origin instead.
 *
 * Prefers the generated merchant.json; falls back to the build-time
 * VITE_PUBLIC_WEB_ORIGIN so single-merchant builds predating ADR 0005 keep
 * working. See ADR 0004 and ADR 0005.
 */
export function storefrontHost() {
  if (!isNative()) {
    // Dev and QA are served from a host that is not a registered storefront, so
    // they name the tenant explicitly. Null in production, where the browser
    // host is the tenant. See env.storefrontDomain.
    if (env.storefrontDomain) {
      try {
        return new URL(`https://${env.storefrontDomain.replace(/^https?:\/\//i, '')}`).hostname;
      } catch {
        return window.location?.hostname || '';
      }
    }
    return window.location?.hostname || '';
  }

  // Precedence: a merchant chosen at runtime (flagship build) beats the one
  // baked at build time (dedicated build), which beats the pre-ADR-0005 env
  // fallback. A dedicated build never populates the first.
  const origin =
    useMerchantStore.getState().selectedDomain || merchantConfig?.domain || env.publicWebOrigin;

  if (!origin) return '';
  try {
    return new URL(origin).hostname;
  } catch {
    return '';
  }
}

/**
 * True when this build is the flagship multi-merchant app rather than a
 * merchant's dedicated app. Set by the generator in merchant.json.
 */
export function isFlagship() {
  return isNative() && merchantConfig?.mode === 'flagship';
}

/**
 * Hosts this build will accept a merchant deep link from.
 *
 * A dedicated app only ever links to its own domain. The flagship app has to
 * accept any merchant's domain, so it matches a suffix instead — without that
 * bound, any link could point the app at an arbitrary host.
 */
export function isTrustedMerchantOrigin(origin) {
  if (!origin) return false;

  let host;
  try {
    host = new URL(origin).hostname;
  } catch {
    return false;
  }

  if (isFlagship()) {
    const suffix = merchantConfig?.linkDomainSuffix;
    return Boolean(suffix) && (host === suffix.replace(/^\./, '') || host.endsWith(suffix));
  }

  const own = merchantConfig?.domain || env.publicWebOrigin;
  if (!own) return false;
  try {
    return new URL(own).hostname === host;
  } catch {
    return false;
  }
}

/**
 * One-shot device position, resolving to a `{ coords: { latitude, longitude } }`
 * shape on both platforms.
 *
 * Web goes through navigator.geolocation. Native must NOT: inside WKWebView
 * that API raises a second, WebKit-level permission prompt on top of the iOS
 * one, and it names the origin — so the user is asked twice and the second
 * prompt reads `"localhost" would like to use your current location`. The
 * native plugin talks to CLLocationManager directly, so there is one prompt
 * and it names the app. See ADR 0004.
 *
 * Rejects when permission is refused or the fix times out. Callers should treat
 * a rejection as "no location available" rather than surfacing it raw.
 */
export async function getCurrentPosition(options = {}) {
  if (!isNative()) {
    if (!navigator.geolocation) {
      throw new Error('Geolocation is unavailable in this browser');
    }
    return new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(resolve, reject, options);
    });
  }

  const { Geolocation } = await import('@capacitor/geolocation');
  const status = await Geolocation.requestPermissions();
  if (status?.location === 'denied') {
    throw new Error('Location permission denied');
  }
  return Geolocation.getCurrentPosition(options);
}

/**
 * Open a URL that the user is expected to come back from — a payment gateway,
 * an OAuth consent screen.
 *
 * Web: a new tab, as before.
 * Native: an in-app browser (SFSafariViewController / Custom Tabs) which keeps
 * the app alive underneath so `appUrlOpen` can hand control back.
 */
export async function openReturnableUrl(url) {
  if (!isNative()) {
    window.open(url, '_blank');
    return;
  }
  const { Browser } = await import('@capacitor/browser');
  await Browser.open({ url, presentationStyle: 'popover' });
}

/** Dismiss the in-app browser. No-op on web. */
export async function closeInAppBrowser() {
  if (!isNative()) return;
  try {
    const { Browser } = await import('@capacitor/browser');
    await Browser.close();
  } catch {
    // Already dismissed by the user — not an error.
  }
}

/**
 * Subscribe to deep links (Universal Links / App Links) opened while the app
 * is running. Returns an unsubscribe function. No-op on web.
 */
export function onAppUrlOpen(handler) {
  if (!isNative()) return () => {};

  let removeListener = null;
  let cancelled = false;

  import('@capacitor/app').then(({ App }) => {
    if (cancelled) return;
    App.addListener('appUrlOpen', (event) => handler(event?.url || '')).then((sub) => {
      if (cancelled) sub.remove();
      else removeListener = () => sub.remove();
    });
  });

  return () => {
    cancelled = true;
    removeListener?.();
  };
}

/**
 * URL the app was cold-launched with, if any. `appUrlOpen` only fires for
 * links received while the app is already running, so a link that starts the
 * app from scratch is only visible here. Resolves null on web.
 */
export async function getLaunchUrl() {
  if (!isNative()) return null;
  try {
    const { App } = await import('@capacitor/app');
    const result = await App.getLaunchUrl();
    return result?.url || null;
  } catch {
    return null;
  }
}

/**
 * Subscribe to hardware/gesture back on Android. Returns an unsubscribe
 * function. No-op on web and iOS.
 */
export function onBackButton(handler) {
  if (!isAndroid()) return () => {};

  let removeListener = null;
  let cancelled = false;

  import('@capacitor/app').then(({ App }) => {
    if (cancelled) return;
    App.addListener('backButton', (event) => handler(event)).then((sub) => {
      if (cancelled) sub.remove();
      else removeListener = () => sub.remove();
    });
  });

  return () => {
    cancelled = true;
    removeListener?.();
  };
}

/** Quit the app. Android only — iOS forbids programmatic exit. */
export async function exitApp() {
  if (!isAndroid()) return;
  const { App } = await import('@capacitor/app');
  await App.exitApp();
}
